<template>
	<!-- *********************************************
	*                Origin Card Block               *
	********************************************** -->
	<divider-generic dividerSize="medium" icon="credit-card" :title="t('cards.transfers.flex.step1.originCardTitle')" :applyBorder="false" id="originCard"/>
	<!-- Origin Card Selector -->
	<dropdown-card
	id="originCard"
	:params="originCardParams"
	:selectedOption="selectedOriginCard"
	:options="props.getCardsList"
	@onBlur="originCardBlurHandler"
	@clicked="[emit('updateData', { fieldName: 'originCard', inputValue: $event }), clearFieldError('originCardParams')]"/>
	<!-- Origin Card Error Message -->
	<infobox-generic v-if="!selectedOriginCard" :label="t('cards.transfers.flex.step1.cardError')" state="negative"/>
	<!-- Details Container-->
	<containers-collapsible
	startClosed
	:title="t('textBlocks.showDetails')"
	:openTitle="t('textBlocks.hideDetails')">
		<!-- Origin Card Details -->
		<text-block-generic
		v-for="(entry, index) in commonCardDetailParser(cardDetails(selectedOriginCard))"
		:key="index.key"
		:params="entry"
		:applyEllipsis="entry.applyEllipsis"
		:mask="entry.mask"/>
	</containers-collapsible>
	<!-- *********************************************
	*             Destination Card Block             *
	********************************************** -->
	<divider-generic dividerSize="medium" icon="credit-card" :title="t('cards.transfers.flex.step1.destinationCardTitle')" :applyBorder="false" id="destinationCard"/>
	<!-- Destination Card Selector -->
	<dropdown-card
	id="destinationCard"
	doubleColumn
	:params="destinationCardParams"
	:selectedOption="selectedDestinationCard"
	:options="showDestinationCardsList"
	@onBlur="destinationCardBlurHandler"
	@clicked="[emit('updateData', { fieldName: 'destinationCard', inputValue: $event }), clearFieldError('destinationCardParams')]"/>
	<!-- Destination Card Error Message -->
	<infobox-generic v-if="!selectedDestinationCard" :label="t('cards.transfers.flex.step1.cardError')" state="negative"/>
	<!-- Details Container-->
	<containers-collapsible
	startClosed
	:title="t('textBlocks.showDetails')"
	:openTitle="t('textBlocks.hideDetails')">
		<!-- Destination Card Details -->
		<text-block-generic
		v-for="(entry, index) in commonCardDetailParser(cardDetails(selectedDestinationCard))"
		:key="index.key"
		:params="entry"
		:applyEllipsis="entry.applyEllipsis"
		:mask="entry.mask"/>
	</containers-collapsible>
	<!-- *********************************************
	*                  Amount Block                  *
	********************************************** -->
	<divider-generic dividerSize="medium" icon="coin-drop" :title="t('cards.transfers.flex.step1.amountTitle')" :applyBorder="false"/>
	<!-- Amount -->
	<input-amount
	id="amount"
	class="amount-input"
	:params="amountParams"
	:inputValue="formData.amount"
	@onBlur="validateAmount"
	@input="[emit('updateData', { fieldName: 'amount', inputValue: $event }), clearFieldError('amountParams')]"/>
</template>

<script setup>
import DividerGeneric from '@/components/generic/dividers/Generic'
import DropdownCard from '@/components/generic/dropdowns/cards/Card'
import ContainersCollapsible from '@/components/generic/containers/Collapsible'
import TextBlockGeneric from '@/components/generic/textBlocks/Generic'
import InputAmount from '@/components/generic/inputs/Amount'
import InfoboxGeneric from '@/components/generic/infobox/Generic'

/* Vue */
import { ref, computed, watch, onMounted } from 'vue'

/* Assets & Helpers */
import { commonCardDetailParser } from '@/assets/scripts/parsers/cards'
import { getElementOffsetTopPosition } from '@/assets/scripts/htmlManipulators'
import { getEmptyFieldsIdentifiers } from '@/assets/scripts/objects'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['updateData', 'formErrorsCleared', 'resetShowMandatoryErrorsProp', 'scrollTo',])

/** Props
@formData {Object} Object with info for our current form
@getCardsList {Array} Array of cards to populate dropdowns
@getFilteredCardsList {Array} Array of cards not containing origin card
@clearFormErrors { Boolean } - Prop to remove of form errors
@mandatoryList { Array } Array of mandatory keys corresponding with our form
@showMandatoryErrors { Boolean } Prop to trigger display of mandatory errors
@isMobile {Boolean}
*/

const props = defineProps({
	formData: Object,
	getCardsList: Array,
	getFilteredCardsList: Array,
	clearFormErrors: Boolean,
	mandatoryList: Array,
	showMandatoryErrors: Boolean,
	isMobile: Boolean
})

/**************************************************
*             Selected option handler             *
**************************************************/
const selectedOriginCard = computed(() => {
	return props.formData.originCard ?? null
})

const selectedDestinationCard = computed(() => {
	return props.formData.destinationCard ?? null
})

const showDestinationCardsList = computed(() => {
	return props.getFilteredCardsList
})

const cardDetails = cardId => {
	return props.getCardsList.find(card => card.id === cardId)
}
/**************************************************
*                  Cards Params                   *
**************************************************/
const originCardParams = ref({
	withBorder: true,
	errorState: false,
	errorMessage: null
})

const destinationCardParams = ref({
	withBorder: true,
	errorState: false,
	errorMessage: null
})
/**************************************************
*                  Amount input                   *
**************************************************/
const amountParams = ref({
	labelText: t('cards.transfers.flex.step1.amount'),
	mandatory: true,
	currency: props.formData.currency,
	errorState: false,
	errorMessage: null
})

/**************************************************
*                  Validations                    *
**************************************************/
const originCardBlurHandler = () => {
	if (selectedOriginCard.value) {
		originCardParams.value.errorState = true
	}
}

const destinationCardBlurHandler = () => {
	if (selectedDestinationCard.value) {
		destinationCardParams.value.errorState = true
	}
}

const validateAmount = () => {
	if (!!props.formData.amount && props.formData.amount <= 0) {
		amountParams.value.errorState = true
		amountParams.value.errorMessage = t('invalidAmount')
	}
}
/**********************************************************************
*                                                                     *
*                        MANDATORY FIELDS LOGIC                       *
*                                                                     *
***********************************************************************/
const allFormFields = ref({
	originCardParams: originCardParams.value,
	destinationCardParams: destinationCardParams.value, 
	amountParams: amountParams.value
})

watch(() => props.showMandatoryErrors, newValue => {
	if (newValue) showMandatoryErrorsHandler()
	emit('resetShowMandatoryErrorsProp')
})

const showMandatoryErrorsHandler = () => {
	/* Logic to scroll to first emptyField */

	const mandatoryFieldsEmptyList = getEmptyFieldsIdentifiers(props.formData, props.mandatoryList)

	if (mandatoryFieldsEmptyList.length) {
		emit('scrollTo', getElementOffsetTopPosition(mandatoryFieldsEmptyList[0]))

		mandatoryFieldsEmptyList.forEach(fieldName => {
			allFormFields.value[`${fieldName}Params`].errorState = true
			if (fieldName === 'amount') {
				allFormFields.value[`${fieldName}Params`].errorMessage = t('mandatoryFieldError')
			}
		})
	}
}
/**********************************************************************
*                                                                     *
*                          CLEAR ERRORS LOGIC                         *
*                                                                     *
***********************************************************************/
watch(() => props.clearFormErrors, () => {
	clearFormErrors()
	emit('formErrorsCleared')
})

const clearFieldError = (fieldName) => {
	allFormFields.value[fieldName].errorMessage = null
	allFormFields.value[fieldName].errorState = false
}

const clearFormErrors = () => {
	for (var fieldName in allFormFields.value) {
		clearFieldError(fieldName)
	}
}

onMounted(() => {
	emit('scrollTo', 0)
})
</script>

<style lang="scss" scoped>
.tertiary-button-wrapper :deep(.button-tertiary) {
	width: fit-content;
}
</style>
